import { apiFetch } from './http';

/** How far a stage or an item is, as FULL_DE_RUTA.md writes it. */
export type RoadmapState =
  'fet' | 'en-proves' | 'en-curs' | 'comencat' | 'seguent' | 'mes-endavant';

export interface RoadmapItem {
  /** What the item's comments hang from. */
  id: string;
  state: RoadmapState;
  text: string;
  /** The progress note the sentence ended with («11 de 16 panells»); null with none. */
  note: string | null;
  comments: number;
  /** How many distinct people tested the item on the running version, from the closed test cards; absent on an older platform. */
  testedBy?: number;
}

export interface RoadmapStage {
  slug: string;
  title: string;
  when: string;
  state: RoadmapState;
  items: RoadmapItem[];
  /** How many proposals of a new item hang from the stage itself. */
  proposals: number;
}

export interface RoadmapRecipe {
  id: string;
  title: string;
  /** `idea` is a planned one the generator cannot build yet. */
  status: 'ready' | 'testing' | 'coming' | 'idea';
}

export interface RoadmapPage {
  now: { title: string; text: string }[];
  /** The headline numbers of the work done; empty when the file has none. */
  figures: { figure: string; label: string }[];
  stages: RoadmapStage[];
  decisions: { date: string | null; text: string }[];
  /** Absent for the copy the platform was built with. */
  updatedAt?: string | null;
  recipes: RoadmapRecipe[];
  /** How many proposals hang from the page itself. */
  general: number;
  /** Only an administrator gets it. */
  admin?: { review: number; unfiled: number; retired: number; problems: string[] };
}

export type CommentKind = 'COMMENT' | 'PROPOSAL';
export type Decision = 'ACCEPTED' | 'DENIED';

export interface RoadmapComment {
  id: number;
  kind: CommentKind;
  itemId?: string | null;
  stageSlug?: string | null;
  body: string;
  createdAt: string;
  editedAt?: string | null;
  author: {
    id: number;
    handle: string | null;
    displayName: string | null;
    avatarUrl: string | null;
  };
  mine: boolean;
  /** The one public trace of a decision: this proposal was taken into the roadmap. */
  accepted: boolean;
  /** Only an administrator gets it; for anybody else the field is not there at all. */
  admin?: {
    decision: Decision | null;
    note: string | null;
    decidedAt: string | null;
    /** An accepted proposal that already has its line in the file. */
    filed: boolean;
    /** The item or stage it hangs from is no longer in the file. */
    retired: boolean;
  };
}

/** Which list: an item's thread, a stage's proposals, the page's own, or one of the administrator's three. */
export type ThreadTarget =
  | { item: string }
  | { stage: string }
  | { general: true }
  | { review: true }
  | { unfiled: true }
  | { retired: true };

function query(target: ThreadTarget): string {
  if ('item' in target) return `item=${encodeURIComponent(target.item)}`;
  if ('stage' in target) return `stage=${encodeURIComponent(target.stage)}`;
  if ('general' in target) return 'general=1';
  if ('review' in target) return 'review=1';
  if ('unfiled' in target) return 'unfiled=1';
  return 'retired=1';
}

export const fetchRoadmap = () => apiFetch<RoadmapPage>('/api/roadmap');

export const fetchRoadmapComments = (target: ThreadTarget) =>
  apiFetch<RoadmapComment[]>(`/api/roadmap/comments?${query(target)}`);

export const postRoadmapComment = (comment: {
  itemId?: string;
  stageSlug?: string;
  kind: CommentKind;
  body: string;
}) => apiFetch<RoadmapComment>('/api/roadmap/comments', { method: 'POST', body: comment });

export const editRoadmapComment = (id: number, body: string) =>
  apiFetch<RoadmapComment>(`/api/roadmap/comments/${id}`, { method: 'PATCH', body: { body } });

export const deleteRoadmapComment = (id: number) =>
  apiFetch<null>(`/api/roadmap/comments/${id}`, { method: 'DELETE' });

/** The administrator's word on a proposal; a null decision takes it back. */
export const decideRoadmapProposal = (
  id: number,
  decision: Decision | null,
  note: string,
  filed = false,
) =>
  apiFetch<RoadmapComment>(`/api/roadmap/comments/${id}/decision`, {
    method: 'POST',
    body: { decision, note, filed },
  });

// --- where the file comes from (Admin, Integracions) ----------------------------------------

export interface RoadmapSourceView {
  repository: string;
  branch: string;
  path: string;
  /** The key is never read back; this says whether there is one, and since when. */
  tokenSet: boolean;
  tokenSetAt: string | null;
  lastCheckAt: string | null;
  /** A code; null when the last check went well. */
  lastError: string | null;
  lastErrorAt: string | null;
  /** Whether the page shows a copy fetched from the repository, not the built-in one. */
  fetched: boolean;
  lastChangeAt: string | null;
  lastCommit: string | null;
  lastCommitAt: string | null;
  /** What the check this answer follows did; null when it follows none. */
  lastResult: string | null;
}

export const fetchRoadmapSource = () => apiFetch<RoadmapSourceView>('/api/admin/roadmap');

/** An empty `token` keeps the stored one. */
export const updateRoadmapSource = (change: {
  repository?: string;
  branch?: string;
  path?: string;
  token?: string;
}) => apiFetch<RoadmapSourceView>('/api/admin/roadmap', { method: 'PUT', body: change });

export const forgetRoadmapToken = () =>
  apiFetch<RoadmapSourceView>('/api/admin/roadmap/token', { method: 'DELETE' });

export const refreshRoadmap = () =>
  apiFetch<RoadmapSourceView>('/api/admin/roadmap/refresh', { method: 'POST' });
